import { cva, type VariantProps } from 'class-variance-authority'
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react'
import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * An inline alert: a signal on its own tint, with its icon and a word
 * (DESIGN.md "The Signal-on-Card Rule", "The Sign-and-Colour Rule").
 *
 * The surface is the signal's subtle tint inside a 1px `--border` frame with
 * 4px corners; the icon carries the signal colour and the text stays ink. No
 * coloured side stripe: DESIGN.md forbids decorative side-stripe borders, and
 * the tint plus the icon already say which signal it is.
 *
 * `info` is the information blue now (`--info`). It was the signal red while
 * the system had no blue; red is never a status any more.
 *
 * `danger` renders as role="alert" (assertive) and the other three as
 * role="status" (polite). A blocking problem interrupts; a confirmation waits
 * its turn.
 */
const alertVariants = cva(
  'animate-enter grid w-full grid-cols-[auto_1fr] items-start gap-x-3 gap-y-0.5 rounded-sm border border-border px-4 py-3',
  {
    variants: {
      severity: {
        info: 'bg-info-subtle [&>svg]:text-info',
        success: 'bg-success-subtle [&>svg]:text-success',
        warning: 'bg-warning-subtle [&>svg]:text-warning',
        danger: 'bg-danger-subtle [&>svg]:text-danger',
      },
    },
    defaultVariants: { severity: 'info' },
  },
)

const ICONS = {
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
} as const

function Alert({
  className,
  severity = 'info',
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  const Icon = ICONS[severity ?? 'info']

  return (
    <div
      data-slot="alert"
      data-severity={severity}
      role={severity === 'danger' ? 'alert' : 'status'}
      className={cn(alertVariants({ severity }), className)}
      {...props}
    >
      <Icon className="mt-px size-4" aria-hidden="true" />
      <div className="grid gap-0.5">{children}</div>
    </div>
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('text-body font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn('text-sm text-foreground', className)}
      {...props}
    />
  )
}

export { Alert, AlertDescription, AlertTitle }
